import { useState, useEffect, useRef, useCallback } from "react";
import { X } from "lucide-react";
import { Badge, Icon, IconButton, Input, cn } from "@winche/ui";
import { fetchOrModels, isFreeModelId, type OrModel } from "../lib/or-models";

export type { OrModel };

export function ModelSearch({ existingIds = new Set(), visionOnly = false, freeOnly = false, paidOnly = false, selectedId = null, onSelect, onClose }: {
  existingIds?: Set<string>;
  visionOnly?: boolean;
  freeOnly?: boolean;
  paidOnly?: boolean;
  /** Currently configured model — shown at the top when the query is empty. */
  selectedId?: string | null;
  onSelect: (model: OrModel) => void;
  onClose: () => void;
}) {
  const [query, setQuery]         = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [models, setModels]       = useState<OrModel[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef      = useRef<HTMLUListElement>(null);

  useEffect(() => {
    fetchOrModels()
      .then(data => setModels(data))
      .catch(() => setError("Failed to load model list."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [onClose]);

  const filtered = useCallback((): OrModel[] => {
    const matchesFilters = (m: OrModel): boolean => {
      if (existingIds.has(m.id)) return false;
      if (visionOnly && !m.supportsVision) return false;
      if (freeOnly && !isFreeModelId(m.id)) return false;
      if (paidOnly && isFreeModelId(m.id)) return false;
      return true;
    };
    const selected = selectedId
      ? (models.find(m => m.id === selectedId) ?? {
          id: selectedId,
          name: selectedId,
          inputPer1M: 0,
          outputPer1M: 0,
          supportsVision: true,
          isFreeVariant: isFreeModelId(selectedId),
          isReasoning: false,
        })
      : null;
    const q = query.trim().toLowerCase();
    if (!q) return selected ? [selected] : [];
    const hits = models.filter(m => {
      if (selected && m.id === selected.id) return false;
      if (!matchesFilters(m)) return false;
      return m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q);
    }).slice(0, 14);
    return selected ? [selected, ...hits] : hits;
  }, [query, models, existingIds, visionOnly, freeOnly, paidOnly, selectedId])();

  const visibleActiveIdx = Math.min(activeIdx, Math.max(filtered.length - 1, 0));

  useEffect(() => {
    const el = listRef.current?.children[visibleActiveIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [visibleActiveIdx]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") { onClose(); return; }
    if (filtered.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[visibleActiveIdx]) { onSelect(filtered[visibleActiveIdx]); onClose(); } }
  }

  function highlight(t: string, q: string): React.ReactNode {
    const idx = t.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1 || !q) return t;
    return (
      <>
        {t.slice(0, idx)}
        <strong className="font-semibold text-brand">{t.slice(idx, idx + q.length)}</strong>
        {t.slice(idx + q.length)}
      </>
    );
  }

  const q = query.trim();
  const hasReasoningInResults = filtered.some((m) => m.isReasoning);

  return (
    <div
      ref={containerRef}
      // Phone: a sheet pinned to the bottom of the screen; wider: a popover
      // under the field that never runs off the edge (admin-11).
      className="fixed inset-x-4 bottom-4 z-50 rounded-xl border border-border bg-popover shadow-popover md:absolute md:inset-x-auto md:bottom-auto md:left-0 md:top-[calc(100%+6px)] md:w-[min(400px,calc(100vw-2rem))]"
    >
      <div className="relative border-b border-border p-2">
        <Input
          type="text"
          aria-label="Search models"
          placeholder={loading ? "Loading models…" : freeOnly ? "Search free tier models" : paidOnly ? "Search paid models" : visionOnly ? "Search vision models" : "Search models"}
          value={query}
          disabled={loading}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIdx(0);
            setError(null);
          }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          spellCheck={false}
          autoFocus
          className="pr-10"
        />
        {query && (
          <IconButton
            label="Clear search"
            variant="ghost"
            size="icon-sm"
            className="absolute right-3 top-1/2 -translate-y-1/2"
            onClick={() => {
              setQuery("");
              setActiveIdx(0);
            }}
          >
            <Icon icon={X} size="sm" />
          </IconButton>
        )}
      </div>

      {error && <p className="px-3 py-2 text-label text-danger-fg">{error}</p>}

      {!loading && q && hasReasoningInResults && (
        <p className="border-b border-border px-3 py-1.5 text-caption text-muted-foreground">
          Reasoning models: effort is capped per operation and thinking is left out of the JSON content.
        </p>
      )}

      {!loading && q && filtered.length === 0 && (
        <p className="p-3 text-label text-muted-foreground">
          No {visionOnly ? "vision " : ""}models found for &ldquo;{q}&rdquo;
        </p>
      )}

      {filtered.length > 0 && (
        <ul ref={listRef} role="listbox" aria-label="Models" className="max-h-72 overflow-y-auto py-1">
          {filtered.map((m, i) => (
            <li
              key={m.id}
              role="option"
              aria-selected={i === visibleActiveIdx}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(m);
                onClose();
              }}
              className={cn(
                "flex min-h-11 cursor-pointer items-center justify-between gap-2 px-3 py-1.5 md:pointer-fine:min-h-0",
                i === visibleActiveIdx && "bg-hover",
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-label font-medium">{highlight(m.name, q)}</div>
                <div className="truncate font-mono text-caption text-muted-foreground">{highlight(m.id, q)}</div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {m.id === selectedId && (
                  <Badge tone="brand" size="sm">
                    selected
                  </Badge>
                )}
                {m.isReasoning && (
                  <Badge tone="outline" size="sm">
                    reasoning
                  </Badge>
                )}
                {m.supportsVision && !visionOnly && (
                  <Badge tone="outline" size="sm">
                    vision
                  </Badge>
                )}
                {m.isFreeVariant ? (
                  <Badge tone="success" size="sm">
                    free tier
                  </Badge>
                ) : (
                  <span className="font-mono text-caption text-muted-foreground">
                    ${m.inputPer1M.toPrecision(3)}/{m.outputPer1M.toPrecision(3)}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
